import { useState } from 'react';
import { Copy, Quote, FileDown, Check } from 'lucide-react';
import type { BookMetadata } from './types';
import { formatTextWithReference } from '../../utils/citation';
import { copyToClipboard } from '../../utils/clipboard';
import ExportReferenceModal from './ExportReferenceModal';
import { useTranslation } from '../../i18n/useTranslation';

interface ReferenceActionBarProps {
  selectedText: string;
  bookMetadata?: BookMetadata | null;
  locationLabel?: string;
}

type Feedback = 'copied-text' | 'copied-ref' | null;

/**
 * Shared action row for Copy / Copy with reference / Export reference.
 * Used by both the text-selection popup and the highlight modal so behaviour
 * stays consistent. Hidden when bookMetadata is missing (nothing to cite).
 */
export default function ReferenceActionBar({
  selectedText,
  bookMetadata,
  locationLabel,
}: ReferenceActionBarProps) {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const showFeedback = (kind: Feedback) => {
    setFeedback(kind);
    setTimeout(() => setFeedback((current) => (current === kind ? null : current)), 1500);
  };

  const handleCopyText = async () => {
    const ok = await copyToClipboard(selectedText);
    if (ok) showFeedback('copied-text');
  };

  const handleCopyWithRef = async () => {
    if (!bookMetadata) return;
    const payload = formatTextWithReference(selectedText, { metadata: bookMetadata, locationLabel });
    const ok = await copyToClipboard(payload);
    if (ok) showFeedback('copied-ref');
  };

  const canCite = !!bookMetadata;

  return (
    <>
      <div className="flex flex-col divide-y divide-neutral-100 border-t border-neutral-200 bg-white">
        <ActionButton
          icon={<Copy className="w-4 h-4" />}
          label={t('App.Reader.ReferenceBar.CopyText')}
          doneLabel={t('App.Reader.ReferenceBar.Copied')}
          done={feedback === 'copied-text'}
          onClick={handleCopyText}
        />
        {canCite && (
          <>
            <ActionButton
              icon={<Quote className="w-4 h-4" />}
              label={t('App.Reader.ReferenceBar.CopyWithReference')}
              doneLabel={t('App.Reader.ReferenceBar.Copied')}
              done={feedback === 'copied-ref'}
              onClick={handleCopyWithRef}
            />
            <ActionButton
              icon={<FileDown className="w-4 h-4" />}
              label={t('App.Reader.ReferenceBar.ExportReference')}
              onClick={() => setExportOpen(true)}
            />
          </>
        )}
      </div>

      {exportOpen && bookMetadata && (
        <ExportReferenceModal
          bookMetadata={bookMetadata}
          locationLabel={locationLabel}
          onClose={() => setExportOpen(false)}
        />
      )}
    </>
  );
}

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  doneLabel?: string;
  done?: boolean;
  onClick: () => void;
}

function ActionButton({ icon, label, doneLabel, done, onClick }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors text-left w-full"
    >
      <span aria-hidden="true" className="text-neutral-500 flex-shrink-0">
        {done ? <Check className="w-4 h-4 text-success-600" /> : icon}
      </span>
      <span>{done && doneLabel ? doneLabel : label}</span>
    </button>
  );
}
